import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { useTranslation } from "react-i18next";
import moment from "moment";

import { isLeavingDateSections, submitFormOnEnter } from "../DatePicker/fieldEvents";

import FormContext from "./FormContext";

const IO_FORMAT = "YYYY-MM-DD";
const DISPLAY_FORMAT = "DD/MM/YYYY";

interface DatePickerProps {
  label: string;
  name: string;
  disableFuture?: boolean;
  disabled?: boolean;
  clearable?: boolean;
}

const DatePicker: FC<DatePickerProps> = ({
  label,
  name,
  disableFuture = false,
  disabled = false,
  clearable = false,
}) => {
  const context = useContext(FormContext);
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  // Bumped when the 2-digit-year correction changes what was typed ("05" -> 2005), so the field
  // re-syncs to the corrected year even when the stored value itself does not change.
  const [resyncKey, setResyncKey] = useState(0);
  // Pickers 9 only keep typed sections that the value prop reflects. An incomplete or invalid entry
  // is reported upwards as null, so while the stored value is null the field gets its own invalid
  // value back; otherwise the year typed into "31/11/2023" would vanish.
  const [draft, setDraft] = useState<moment.Moment | null>(null);

  // Initialize the value properly from context - only once
  useEffect(() => {
    const { value } = context.get(name);
    if (value) {
      const momentObj = moment.isMoment(value) ? value : moment(value);
      if (momentObj.isValid()) {
        context.set(name, momentObj.format(IO_FORMAT));
      }
    }
  }, []);

  const correctYear = (date: moment.Moment): moment.Moment => {
    const year = date.year();
    if (year > 0 && year < 100) {
      return date.clone().year(year + 2000);
    }
    return date;
  };

  const toStoredValue = (date: moment.Moment): string => {
    const corrected = correctYear(date);
    if (corrected.year() !== date.year()) setResyncKey((key) => key + 1);
    return corrected.startOf("day").format(IO_FORMAT);
  };

  // Handle change event handles calendar input
  const handleChange = (momentDate: moment.Moment | null) => {
    if (momentDate && momentDate.isValid()) {
      setDraft(null);
      context.set(name, toStoredValue(momentDate));
    } else {
      setDraft(momentDate);
      context.set(name, null);
    }
  };

  // Handles keyboard input: validates what was typed, read from the field's hidden input (the
  // formatted sections, e.g. "16/MM/YYYY" while incomplete), so the invalid/future-date messages stay
  // the translated ones.
  const handleBlur = (event: React.FocusEvent<HTMLElement>) => {
    if (!isLeavingDateSections(event)) return;
    const inputValue = inputRef.current?.value ?? "";

    if (inputValue && inputValue.trim() !== "") {
      const parsedDate = moment(inputValue, DISPLAY_FORMAT, true);

      if (!parsedDate.isValid()) {
        context.set(name, null);
        context.setError(name, t("invalid_date"));
        return;
      }

      if (disableFuture && parsedDate.isAfter(moment(), "day")) {
        // The picker accepted this date, so there is no draft: keep the typed date visible.
        setDraft(parsedDate);
        context.set(name, null);
        context.setError(name, t("future_date_not_allowed"));
        return;
      }

      context.set(name, toStoredValue(parsedDate));
      context.setError(name, "");
    }
  };

  const { value, error } = context.get(name);
  // Memoised (and never undefined) so the field only re-syncs its sections when the value (or
  // resyncKey) changes.
  const dateValue = useMemo(
    () => (value ? (moment.isMoment(value) ? value : moment(value, IO_FORMAT)) : null),
    // resyncKey is a deliberate extra dependency: a new moment instance makes the field re-sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, resyncKey],
  );

  return (
    <DesktopDatePicker
      format={DISPLAY_FORMAT}
      value={!value && draft ? draft : dateValue}
      disableFuture={disableFuture}
      disabled={disabled}
      label={label}
      onChange={handleChange}
      inputRef={inputRef}
      slotProps={{
        textField: {
          helperText: error || DISPLAY_FORMAT.toLowerCase(),
          fullWidth: true,
          size: "small",
          error: !!error,
          onBlur: handleBlur,
          onKeyDown: submitFormOnEnter,
          sx: { minWidth: "250px" },
        },
        field: clearable
          ? {
              clearable: true,
              onClear: () => {
                setDraft(null);
                context.set(name, null);
              },
            }
          : undefined,
      }}
    />
  );
};

export default DatePicker;
